import { useState, useEffect } from "react";
import { useQuery } from "@apollo/client";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { GET_PROJECTS } from "../../../GraphQL/settings/queries";

import { Archive, Pencil, Users } from "lucide-react";
import { EToastColorStatus, EToastPosition, IToastData, IProject } from "../../../interfaces";
import AddProjectModal from "./AddProjectModal";
import EditProjectModal from "./EditProjectModal";
import ArchiveProjectModal from "./ArchiveProjectModal";
import MemberProjectModal from "./MemberProjectModal";
import Notification from "../../Notification";
import InviteMemberModal from "./InviteMemberModal";

const Projects = () => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [projectId, setProjectId] = useState<number>(0);
  const [projectList, setProjectList] = useState([] as IProject[]);
  const [toastData, setToastData] = useState<IToastData>({
    show     : false,
    color    : EToastColorStatus.SUCCESS,
    position : EToastPosition.BOTTOM_END,
    message  : "",
  });

  const { loading, error, data, refetch } = useQuery(GET_PROJECTS, {
    variables: { skip: 0, take: 20 },
  });

  useEffect(() => {
    setProjectList(data?.projects?.items);
  }, [data]);

  const handleShowToast = (
    color    : EToastColorStatus,
    position : EToastPosition,
    message  : string,
  ) => {
    setToastData({ show: true, color, position, message });
  };

  const handleAddProjectModal = () => {
    setShowAddModal(true);
  }

  const handleEditProjectModal = (id: number) => {
    setProjectId(id);
    setShowEditModal(true);
  }

  const handleOpenMemberUsers = (id: number) => {
    setProjectId(id);
    setShowMemberModal(true);
  }

  const handleArchiveProjectModal = (id: number) => {
    setProjectId(id);
    setShowArchiveModal(true);
  }

  return (
    <Card className="border-0">
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-1/2">Name</TableHead>
              <TableHead className="w-1/2 text-center">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell>Loading</TableCell></TableRow>}
            {error && <TableRow><TableCell>Error: {error?.message}</TableCell></TableRow>}
            {!loading && !error && projectList && projectList.map((item) => (
              <TableRow key={item?.id}>
                <TableCell>{item?.title}</TableCell>
                <TableCell className="text-center">
                  <Button
                    size="sm"
                    variant="secondary"
                    className="mx-1"
                    onClick={() => handleEditProjectModal(item?.id)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    className="mx-1"
                    onClick={() => handleArchiveProjectModal(item?.id)}
                  >
                    <Archive className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    className="mx-1"
                    onClick={() => handleOpenMemberUsers(item?.id)}
                  >
                    <Users className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <div className="flex justify-end pt-lg border-t border-slate-200 dark:border-slate-700">
          <Button
            size="sm"
            onClick={handleAddProjectModal}
          >
            Add Project
          </Button>
        </div>
      </CardContent>

      <AddProjectModal
        showAddModal={showAddModal}
        onCloseAddModal={() => setShowAddModal(false)}
        reloadProjects={refetch}
        onHandleCreateToast={(
          color    : EToastColorStatus,
          position : EToastPosition,
          message  : string,
        ) => handleShowToast(color, position, message)}
      />

      <EditProjectModal
        showEditModal={showEditModal}
        onCloseModal={() => setShowEditModal(false)}
        projectId={projectId}
        projectList={projectList}
        reloadProjects={refetch}
        onHandleCreateToast={(
          color    : EToastColorStatus,
          position : EToastPosition,
          message  : string,
        ) => handleShowToast(color, position, message)}
      />

      <ArchiveProjectModal
        showArchiveModal={showArchiveModal}
        onCloseModal={() => setShowArchiveModal(false)}
        projectId={projectId}
        reloadProjects={refetch}
        onHandleCreateToast={(
          color    : EToastColorStatus,
          position : EToastPosition,
          message  : string,
        ) => handleShowToast(color, position, message)}
      />

      <MemberProjectModal
        showMemberModal={showMemberModal}
        onHandleInviteMember={() => setShowInviteModal(true)}
        onCloseModal={() => setShowMemberModal(false)}
      />

      <InviteMemberModal
        showInviteModal={showInviteModal}
        onCloseModalInvite={() => setShowInviteModal(false)}
        projectId={projectId}
        onHandleCreateToast={(
          color    : EToastColorStatus,
          position : EToastPosition,
          message  : string,
        ) => handleShowToast(color, position, message)}
      />

      <Notification
        delay={3000}
        toastData={toastData}
        onClose={() => setToastData({ ...toastData, show: false })}
      />
    </Card>
  )
}

export default Projects;
